export interface Volume {
  number: string
  title: string
  subtitle: string
  bullets: string[]
  result: string
  featured?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Massage Therapy Foundations & Professional Practice',
    subtitle:
      'The professional foundation behind every well-structured session.',
    bullets: [
      'Treatment room preparation and client intake',
      'Consent and professional boundaries',
      'Positioning and draping',
      'Therapist body mechanics',
      'Pressure and contact quality',
      'Oils, lotions, and massage media',
      'Complete session structure and professional transitions',
      'Contraindications and cautions',
      'Session closing, review, and between-client reset',
    ],
    result:
      'You stop treating massage as a collection of isolated techniques and begin working from a clear professional framework.',
  },
  {
    number: 'Volume 2',
    title: 'Applied Anatomy, Muscles & Movement',
    subtitle: 'Practical anatomy you can actually use during massage.',
    bullets: [
      'Major massage-relevant muscles and regional anatomy maps',
      'Anatomical landmarks and palpation orientation',
      'Muscle actions and movement relationships',
      'Neck, shoulder, upper back, and arm anatomy',
      'Spine, core, pelvis, hips, and glutes',
      'Thighs, lower legs, and feet',
      'Functional movement observation',
      'Printable visual references',
    ],
    result:
      'You understand what is beneath your hands, how regions connect, and how anatomy supports better professional decisions.',
  },
  {
    number: 'Volume 3',
    title: 'Essential Massage Techniques & Technique Sequences',
    subtitle:
      'Stop using techniques in isolation and start building professional flow.',
    bullets: [
      'Gliding, kneading, compression, and friction',
      'Sustained pressure, percussion, and vibration',
      'Gentle movement and pressure control',
      'Therapist contact tools',
      'Professional transitions between techniques',
      'Regional sequences and full-body sequencing',
      'Technique mistakes and corrections',
    ],
    result:
      'You stop asking “What do I do next?” and begin connecting techniques with purpose.',
  },
  {
    number: 'Volume 4',
    title: 'Client Assessment, Session Planning & Adaptation',
    subtitle:
      'Turn client information into a clear professional session plan.',
    bullets: [
      'Professional intake and goal identification',
      'Body-region prioritization',
      'Movement and posture observation',
      'Session-duration decisions',
      'Technique, pressure, and positioning selection',
      'Real-time adaptation during the appointment',
      '30-, 45-, 60-, and 90-minute planning',
      'Professional safety decisions, review, and documentation',
    ],
    result:
      'You gain a repeatable process for deciding what to focus on, what to modify, and how to organize the appointment.',
  },
  {
    number: 'Volume 5',
    title: '150 Ready-to-Use Massage Sessions',
    subtitle:
      'The practical heart of the system: choose the goal, open the session, follow the structure, adapt to the client.',
    bullets: [
      'Full-body relaxation sessions',
      'Neck, shoulders, upper back, back, and posterior trunk',
      'Arms, forearms, hands, hips, glutes, and pelvic region',
      'Thighs, legs, and feet',
      'Sports and post-workout recovery',
      'Stress reduction, calming, and mobility-focused work',
      'Gentle, low-intensity, and short targeted sessions',
      'Sessions ranging from approximately 20 to 90 minutes',
    ],
    result:
      'You no longer have to create every appointment from scratch.',
    featured: true,
  },
  {
    number: 'Volume 6',
    title: 'Special Populations & Advanced Session Adaptations',
    subtitle: 'Same professional goal. Different delivery.',
    bullets: [
      'First-time and highly sensitive clients',
      'Older adults and limited mobility',
      'Desk workers, athletes, and active clients',
      'Pregnancy-aware contexts',
      'High-stress clients, fatigue, and deep-pressure requests',
      'Side-lying, seated, and semi-reclined sessions',
      '20 adapted session plans',
      '25 common adaptation mistakes and corrections',
    ],
    result: 'You become more adaptable without becoming random.',
  },
  {
    number: 'Volume 7',
    title: 'Advanced Practice, Teaching & Client Experience',
    subtitle: 'Bring the entire professional system together.',
    bullets: [
      'Advanced session design and custom session building',
      'Technique sequencing and professional communication',
      'Client experience and repeat-client consistency',
      'Documentation systems',
      'Teaching massage techniques and demonstration systems',
      'Student practice, professional feedback, 30 teaching corrections',
      'Skill assessment and complete-session teaching',
      'Efficiency, sustainability, and professional boundaries',
    ],
    result:
      'You move from simply performing massage to working with a complete professional system.',
  },
]

export interface LessonGroup {
  range: string
  title: string
  detail: string
}

export const lessonGroups: LessonGroup[] = [
  {
    range: 'Full body',
    title: 'Full-Body Relaxation',
    detail: 'Complete sessions focused on relaxation, pacing, and flow.',
  },
  {
    range: 'Upper body',
    title: 'Neck, Shoulders & Upper Back',
    detail: 'Structured sessions for common upper-body goals.',
  },
  {
    range: 'Posterior body',
    title: 'Back & Posterior Trunk',
    detail: 'Regional and integrated posterior-body work.',
  },
  {
    range: 'Upper limbs',
    title: 'Arms, Forearms & Hands',
    detail: 'Useful for repetitive-use and upper-limb recovery contexts.',
  },
  {
    range: 'Hips',
    title: 'Hips & Glutes',
    detail: 'Professional sessions centered on hip and gluteal regions.',
  },
  {
    range: 'Lower body',
    title: 'Thighs, Legs & Feet',
    detail: 'Complete lower-body session options.',
  },
  {
    range: 'Recovery',
    title: 'Sports & Post-Workout Recovery',
    detail: 'Structured recovery-oriented sessions for active clients.',
  },
  {
    range: 'Calming',
    title: 'Stress Reduction & Calming',
    detail: 'Slower pacing and lower-intensity approaches.',
  },
  {
    range: 'Movement',
    title: 'Mobility-Focused Sessions',
    detail: 'Movement-integrated professional massage planning.',
  },
  {
    range: 'Comfort first',
    title: 'Gentle & Low-Intensity',
    detail: 'Comfort-first session options for sensitive clients.',
  },
  {
    range: 'Short format',
    title: 'Short Targeted Sessions',
    detail: 'Focused sessions for limited appointment time.',
  },
]
